import React, { useState, useCallback, useRef, useEffect } from 'react'
import { DocumentEditor } from '@mes-amis/super-form'
import '@mes-amis/super-form/styles.css'
import './style.scss'
import createChannel from '../../../../cables/cable'

const CopyableClientId = ({ slug }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(slug).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [slug])

  if (!slug) return null

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded border border-gray-200 hover:bg-gray-200 cursor-pointer"
      title="Copy Client ID"
    >
      {copied ? 'Copied!' : `id: ${slug}`}
    </button>
  )
}

const ReactDocumentEditor = (props) => {
  const {
    template, document: doc, client, urls, csrfToken, signedStreamName, readonly, fieldPermissions,
    title, clientName, clientSlug, programName, defaultPanelOpen,
    dateCompleted, reopened, minorChange, hasSignatureConfig
  } = props

  const [formData, setFormData] = useState(doc?.super_form || {})
  const [errors, setErrors] = useState({})
  const dateCompletedRef = useRef(dateCompleted || '')
  const saveTimeoutRef = useRef(null)
  const latestFormDataRef = useRef(formData)
  const lastSavedDataRef = useRef(doc?.super_form || {})
  const lastRevisionRef = useRef(doc?.revision || null)

  // Subscribe to real-time document updates via ActionCable
  useEffect(() => {
    if (!signedStreamName) return

    const subscription = createChannel(
      { channel: 'Turbo::StreamsChannel', signed_stream_name: signedStreamName },
      {
        received(data) {
          try {
            const message = typeof data === 'string' ? JSON.parse(data) : data
            if (message?.type !== 'document_update' || !message.document) return

            const serverDoc = message.document
            const serverRevision = serverDoc.revision || null

            // Skip echoes of our own saves
            if (serverRevision && serverRevision === lastRevisionRef.current) return

            const serverFormData = serverDoc.super_form
            if (!serverFormData || typeof serverFormData !== 'object') return

            lastRevisionRef.current = serverRevision

            // Merge server data into local state, preserving any local unsaved changes
            setFormData((current) => {
              const merged = { ...current }
              for (const [key, value] of Object.entries(serverFormData)) {
                // Accept server value unless we have a local unsaved change for this field
                const localUnsaved = JSON.stringify(current[key]) !== JSON.stringify(lastSavedDataRef.current[key])
                if (!localUnsaved) {
                  merged[key] = value
                }
              }
              latestFormDataRef.current = merged
              lastSavedDataRef.current = { ...lastSavedDataRef.current, ...serverFormData }
              return merged
            })
          } catch (error) {
            console.error('[ReactDocumentEditor] Failed to process broadcast:', error)
          }
        }
      }
    )

    return () => {
      if (subscription) subscription.unsubscribe()
    }
  }, [signedStreamName])

  const performSave = useCallback(async (data) => {
    const changedFields = {}
    for (const [key, value] of Object.entries(data)) {
      if (JSON.stringify(value) !== JSON.stringify(lastSavedDataRef.current[key])) {
        changedFields[key] = value
      }
    }
    if (Object.keys(changedFields).length === 0) return

    try {
      const tokenEl = window.document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.content : csrfToken

      const response = await fetch(urls.autosave, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ document: { super_form: changedFields } })
      })

      if (response.ok || response.status === 204) {
        lastSavedDataRef.current = { ...lastSavedDataRef.current, ...changedFields }
      }

      if (!response.ok && response.status === 401) {
        window.location.reload()
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Autosave failed:', error)
    }
  }, [urls.autosave, csrfToken])

  const scheduleSave = useCallback((data) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => performSave(data), 900)
  }, [performSave])

  const handleFormDataChange = useCallback((field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value }
      latestFormDataRef.current = updated
      scheduleSave(updated)
      return updated
    })
  }, [scheduleSave])

  const handleBlur = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    performSave(latestFormDataRef.current)
  }, [performSave])

  const saveDateCompleted = useCallback(async (value) => {
    try {
      const tokenEl = window.document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.content : csrfToken

      const response = await fetch(urls.autosave, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ document: { recorded_at: value || null } })
      })

      if (!response.ok && response.status === 401) {
        window.location.reload()
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Date completed save failed:', error)
    }
  }, [urls.autosave, csrfToken])

  const handleFinish = useCallback(async (validatedData) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)

    try {
      const tokenEl = window.document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.content : csrfToken

      const response = await fetch(urls.finalize, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'text/html, application/json'
        },
        body: JSON.stringify({ document: { super_form: validatedData } }),
        redirect: 'follow'
      })

      if (response.redirected) {
        window.location.href = response.url
      } else if (response.ok) {
        window.location.href = urls.documents
      } else {
        const data = await response.json().catch(() => ({}))
        if (data.errors) setErrors(data.errors)
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Finalize failed:', error)
    }
  }, [urls.finalize, urls.documents, csrfToken])

  const handleSaveAndExit = useCallback(async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    await performSave(latestFormDataRef.current)
    window.location.href = urls.documents
  }, [performSave, urls.documents])

  const handleBack = useCallback(() => {
    window.location.href = urls.documents
  }, [urls.documents])

  const queryModelRecords = useCallback(async (model, opts) => {
    if (model === 'Contact' && urls.contacts && opts.clientId) {
      try {
        const response = await fetch(urls.contacts, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken }
        })
        if (response.ok) {
          const data = await response.json()
          return data.map((c) => ({
            id: c.id,
            label: c.display_name || `${c.first_name} ${c.last_name}`,
            data: c
          }))
        }
      } catch (error) {
        console.error('[ReactDocumentEditor] Failed to query model records:', error)
      }
    }
    return []
  }, [urls.contacts, csrfToken])

  const renderActions = useCallback(() => (
    urls.toggleReactSuperForm ? (
      <a
        href={urls.toggleReactSuperForm}
        data-turbo-method="post"
        className="text-sm text-gray-500 hover:text-gray-700 underline"
      >
        Switch to Classic Editor
      </a>
    ) : null
  ), [urls.toggleReactSuperForm])

  const renderTitleContent = useCallback(() => (
    <div className="flex flex-col">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">{clientName}</h1>
        <CopyableClientId slug={clientSlug} />
      </div>
      <span className="text-sm">
        <span className="text-gray-500">{title}</span>
        {programName && <span className="text-gray-400"> — {programName}</span>}
      </span>
    </div>
  ), [clientName, clientSlug, title, programName])

  const renderBeforeForm = useCallback(() => {
    const banners = []

    if (reopened && hasSignatureConfig) {
      banners.push(
        <div key="reopened-sig" className="p-4 bg-gray-100 rounded-md mb-4">
          <div className="flex items-center">
            <span className="mr-2">&#9888;</span>
            {minorChange ? (
              <span>This document is now re-opened for <strong>minor changes</strong>. The update won't require redoing signatures.</span>
            ) : (
              <span>This document is now <strong>re-opened</strong> for editing. After making the changes, re-sign it again. You can access the previous version in the document version history.</span>
            )}
          </div>
        </div>
      )
    } else if (reopened) {
      banners.push(
        <div key="reopened" className="p-4 bg-gray-100 rounded-md mb-4">
          <div className="flex items-center">
            <span className="mr-2">&#9888;</span>
            <span>This document is now <strong>re-opened</strong> for editing. You can access the previous version in the document version history.</span>
          </div>
        </div>
      )
    }

    if (dateCompleted) {
      banners.push(
        <div key="date-completed" className="mb-4">
          <label htmlFor="date-completed" className="flex justify-between text-sm font-semibold text-black mb-2">
            <span>Date Completed</span>
          </label>
          <input
            id="date-completed"
            type="date"
            defaultValue={dateCompletedRef.current}
            onBlur={(e) => {
              if (e.target.value !== dateCompletedRef.current) {
                saveDateCompleted(e.target.value)
                dateCompletedRef.current = e.target.value
              }
            }}
            readOnly={readonly}
            className={`block w-full rounded-md border shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 ${
              readonly ? 'border-gray-300 bg-gray-50 cursor-not-allowed text-gray-700' : 'border-gray-300 bg-white'
            }`}
          />
          <p className="mt-1 text-xs text-gray-500">
            {readonly
              ? 'Re-open this document to edit the date originally completed.'
              : 'The date originally completed. This can be different from the date you submit the form in the system.'}
          </p>
        </div>
      )
    }

    return banners.length > 0 ? <>{banners}</> : null
  }, [dateCompleted, saveDateCompleted, readonly, reopened, minorChange, hasSignatureConfig])

  return (
    <div className="react-super-form-container super-form" data-testid="react-document-editor">
      <DocumentEditor
        template={template}
        document={doc}
        formData={formData}
        onFormDataChange={handleFormDataChange}
        onBlur={handleBlur}
        onFinish={handleFinish}
        onSaveAndExit={handleSaveAndExit}
        onBack={handleBack}
        renderTitleContent={renderTitleContent}
        defaultPanelOpen={defaultPanelOpen}
        renderBeforeForm={renderBeforeForm}
        renderActions={renderActions}
        client={client}
        readonly={readonly}
        errors={errors}
        fieldPermissions={fieldPermissions}
        queryModelRecords={queryModelRecords}
        showSummary={template.show_summary !== false && (
          !!template.super_form?.definition?.summary ||
          (template.super_form?.definition?.interrai?.algorithms?.length ?? 0) > 0
        )}
        saveAndExitLabel="Save and Exit"
        finishLabel="Finish Document"
        saveAndExitBtnClassName="react-sf-btn react-sf-btn--secondary"
        finishBtnClassName="react-sf-btn react-sf-btn--primary"
      />
    </div>
  )
}

export default ReactDocumentEditor
